import type { DayRow } from '../lib/types';
import type { AdminEmployee } from '../types/models';
import type { EmployeeReportAssignment } from './reportAssignments';
import { isAssignmentActiveInPeriod, isAssignmentActiveOnDate } from './reportAssignments';

export type CareCategory = 'care' | 'nurse';
export type StaffingMode = 'fulltime_dedicated' | 'fulltime_concurrent' | 'parttime';

export interface StaffingEntry {
  key: string;
  employee: AdminEmployee;
  category: CareCategory;
  mode: StaffingMode;
  allocationRatio: number;
  assignment: EmployeeReportAssignment | null;
}

export interface StaffingCategorySummary {
  fulltimeDedicatedCount: number;
  concurrentHours: number;
  concurrentEquiv: number;
  parttimeHours: number;
  parttimeEquiv: number;
}

export interface StaffingSummary {
  care: StaffingCategorySummary;
  nurse: StaffingCategorySummary;
}

const isFullTime = (employee: AdminEmployee): boolean =>
  employee.employmentType === '正社員' || employee.employmentType === 'その他';

const classifyCurrentJob = (jobType: string | null | undefined): CareCategory | null => {
  const value = jobType ?? '';
  if (value.includes('介護')) return 'care';
  if (value.includes('看護')) return 'nurse';
  return null;
};

export const buildStaffingEntries = (
  employees: AdminEmployee[],
  assignments: EmployeeReportAssignment[],
  periodStart: string,
  periodEnd: string
): StaffingEntry[] => {
  const assignmentsByEmployee = new Map<string, EmployeeReportAssignment[]>();
  assignments
    .filter(assignment => isAssignmentActiveInPeriod(assignment, periodStart, periodEnd))
    .forEach(assignment => {
      const current = assignmentsByEmployee.get(assignment.employeeId) ?? [];
      current.push(assignment);
      assignmentsByEmployee.set(assignment.employeeId, current);
    });

  return employees.flatMap<StaffingEntry>(employee => {
    const employeeAssignments = assignmentsByEmployee.get(employee.id) ?? [];

    if (employeeAssignments.length > 0) {
      return employeeAssignments
        .filter(assignment => assignment.reportRole === 'care' || assignment.reportRole === 'nurse')
        .map(assignment => {
          const category: CareCategory = assignment.reportRole === 'care' ? 'care' : 'nurse';
          const mode: StaffingMode = !isFullTime(employee)
            ? 'parttime'
            : assignment.workMode === 'concurrent'
              ? 'fulltime_concurrent'
              : 'fulltime_dedicated';
          return {
            key: `${employee.id}:${assignment.id}`,
            employee,
            category,
            mode,
            allocationRatio: assignment.allocationRatio ?? 1,
            assignment
          };
        });
    }

    const category = classifyCurrentJob(employee.jobType);
    if (!category) return [];

    return [{
      key: `${employee.id}:current-job`,
      employee,
      category,
      mode: isFullTime(employee) ? 'fulltime_dedicated' : 'parttime',
      allocationRatio: 1,
      assignment: null
    }];
  });
};

export const getMonthlyMinutes = (days: DayRow[]): number =>
  days.reduce((sum, day) => sum + (Number(day.work_minutes) || 0), 0);

export const getAllocatedMinutes = (entry: StaffingEntry, days: DayRow[]): number =>
  days.reduce((sum, day) => {
    if (entry.assignment && !isAssignmentActiveOnDate(entry.assignment, day.work_date)) {
      return sum;
    }
    return sum + (Number(day.work_minutes) || 0) * entry.allocationRatio;
  }, 0);

const createEmptyCategorySummary = (): StaffingCategorySummary => ({
  fulltimeDedicatedCount: 0,
  concurrentHours: 0,
  concurrentEquiv: 0,
  parttimeHours: 0,
  parttimeEquiv: 0
});

export const summarizeStaffingEntries = (
  entries: StaffingEntry[],
  daysByEmployee: Record<string, DayRow[]>,
  baseHours: number
): StaffingSummary => {
  const result: StaffingSummary = {
    care: createEmptyCategorySummary(),
    nurse: createEmptyCategorySummary()
  };

  entries.forEach(entry => {
    const target = result[entry.category];
    if (entry.mode === 'fulltime_dedicated') {
      target.fulltimeDedicatedCount += 1;
      return;
    }

    const hours = getAllocatedMinutes(entry, daysByEmployee[entry.employee.id] ?? []) / 60;
    if (entry.mode === 'fulltime_concurrent') {
      target.concurrentHours += hours;
    } else {
      target.parttimeHours += hours;
    }
  });

  (['care', 'nurse'] as CareCategory[]).forEach(category => {
    const target = result[category];
    target.concurrentEquiv = baseHours > 0 ? target.concurrentHours / baseHours : 0;
    target.parttimeEquiv = baseHours > 0 ? target.parttimeHours / baseHours : 0;
  });

  return result;
};

export const staffingTotalEquiv = (summary: StaffingCategorySummary): number =>
  summary.fulltimeDedicatedCount + summary.concurrentEquiv + summary.parttimeEquiv;
